/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Input,
  Layout,
  Space,
  Table,
  DatePicker,
  Checkbox,
  Select,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { getAPI } from "../../../utils/common";
import LayoutHeader from "../../inc/LayoutHeader";
import LayoutFooter from "../../inc/LayoutFooter";
import { BOOKINGS } from "../../../constants/api";
import {
  bookingStatusText,
  CURRENCY,
  getPayTypeLabel,
  getRoomCapicityLabel,
} from "../../../constants/constent";
import { INFO_MSG_TYPE } from "../../../constants/messages";
import { displayMessage } from "../../../utils/common";
import { unparse } from "papaparse";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(localizedFormat);
function ReservationTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  const [state, setState] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [cancelledBookings, setCancelledBookings] = useState(false);
  const [filterType, setFilterType] = useState("Check In");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "days"),
    dayjs(),
  ]);

  useEffect(() => {
    if (type) getData();
  }, [type]);

  const getData = () => {
    setLoading(true);

    const [startDate, endDate] = dateRange;

    // Convert dayjs dates to YYYY-MM-DD
    const start = startDate.format("YYYY-MM-DD");
    const end = endDate.format("YYYY-MM-DD");

    const url = `${BOOKINGS}?filterType=${filterType}&startDate=${start}&endDate=${end}`;

    const successFn = (data) => {
      if (data.status) {
        const bookings = data.data.map((row, index) => ({
          key: index,
          booking_id: row.order_id,
          name: row.customer_name,
          booked_on: dayjs.utc(row.created_at).format("ll, LT"),
          checkin: dayjs.utc(row.checkin_date).format("ll, LT"),
          checkout: dayjs.utc(row.checkout_date).format("ll, LT"),
          source: row.booking_source.toUpperCase() || "PMS",
          segment: row.booking_segment.toUpperCase(),
          guests: getRoomCapicityLabel(row.room_type),
          rooms: row.room_no,
          nights: row.total_days,
          amount: `${CURRENCY} ${parseFloat(
            row.total_with_gst
          ).toLocaleString()}`,
          payment_mode: getPayTypeLabel(row.payment_method) || "NA",
          status: bookingStatusText[row.booking_status] || "Unassigned",
        }));
        setState(bookings);
        setFilteredData(bookings);
      } else {
        displayMessage(INFO_MSG_TYPE, data.message);
      }
      setLoading(false);
    };

    const errorFn = () => {
      setLoading(false);
    };

    getAPI(url, successFn, errorFn);
  };

  useEffect(() => {
    applyFilters();
  }, [searchText, cancelledBookings]);

  const applyFilters = () => {
    let data = [...state];

    // Cancelled bookings filter
    if (cancelledBookings) {
      data = data.filter((row) => row.status === "Cancelled");
    }

    // Search filter
    if (searchText) {
      data = data.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    setFilteredData(data);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleDownload = () => {
    if (filteredData.length === 0) {
      displayMessage(INFO_MSG_TYPE, "No data to export");
      return;
    }

    const exportData = filteredData.map((row) => ({
      "Booking ID": row.booking_id,
      "Customer Name": row.name,
      "Booked On": row.booked_on,
      "Check-In": row.checkin,
      "Check-Out": row.checkout,
      Guests: row.guests,
      Segment: row.segment,
      Rooms: row.rooms,
      Nights: row.nights,
      Amount: row.amount,
      "Payment Mode": row.payment_mode,
      Status: row.status,
    }));

    const csv = unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "reservations.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: "Booking ID",
      dataIndex: "booking_id",
      render: (text) => <a>{text}</a>,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
    },
    {
      title: "Booked On",
      dataIndex: "booked_on",
    },
    {
      title: "CheckIn",
      dataIndex: "checkin",
    },
    {
      title: "CheckOut",
      dataIndex: "checkout",
    },
    {
      title: "Source",
      dataIndex: "source",
    },
    {
      title: "Segment",
      dataIndex: "segment",
    },
    {
      title: "Room",
      dataIndex: "rooms",
    },
    {
      title: "Nights",
      dataIndex: "nights",
    },
    {
      title: "Amount",
      dataIndex: "amount",
    },
    {
      title: "Payment Mode",
      dataIndex: "payment_mode",
    },
    {
      title: "Status",
      dataIndex: "status",
    },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Reservation Data"
          extra={
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
              />
              <Select value={filterType} onChange={setFilterType}>
                <Option value="Booking Date">Booking Date</Option>
                <Option value="Check In">Check In</Option>
                <Option value="Check Out">Check Out</Option>
                <Option value="Stay Date">Stay Date</Option>
              </Select>
              <Button onClick={getData}>Submit</Button>
              <Input
                placeholder="Search"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <Checkbox
                checked={cancelledBookings}
                onChange={(e) => setCancelledBookings(e.target.checked)}
              >
                Cancelled bookings
              </Checkbox>
              <Button onClick={handleDownload}>Download</Button>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default ReservationTable;
